import React from 'react';
import { useTheme } from '@/context/ThemeContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/shared/SEOHead';
import { Shield, Smartphone, Activity, BarChart3, Users, Phone, Lock, Heart } from 'lucide-react';

export default function PrivacidadFitRewardsPage() {
  const { currentTheme: t, theme } = useTheme();

  const headingStyle = { color: t.text, fontFamily: 'Manrope, sans-serif' };
  const textStyle = { color: t.textSecondary, fontFamily: 'Inter, sans-serif' };
  const strongStyle = { color: t.text };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <SEOHead
        title="Política de Privacidad FitRewards | Motivy"
        description="Política de privacidad de FitRewards by Motivy. Conoce cómo manejamos tus datos de salud, actividad física y bienestar."
        keywords="política de privacidad, FitRewards, datos de salud, HealthKit, Google Fit, privacidad"
      />

      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-12" style={{ background: t.heroBg }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-6"
            style={{ background: t.badgeBg, color: t.badgeText }}
          >
            <Shield size={14} /> FitRewards
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl lg:text-5xl font-bold mb-4" style={headingStyle}>
            Política de Privacidad
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg" style={{ color: t.textSecondary }}>
            FitRewards by Motivy — Cómo protegemos tu información
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" style={{ background: t.bg }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Info Cards */}
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {[
              { icon: Lock, label: 'Datos protegidos', desc: 'Encriptación end-to-end' },
              { icon: Shield, label: 'GDPR Compliant', desc: 'Cumplimiento normativo' },
              { icon: Users, label: 'Sin terceros', desc: 'No vendemos tus datos' },
              { icon: Phone, label: 'Contacto directo', desc: 'info@motivy.co' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: t.featureIconBg }}>
                  <item.icon size={18} style={{ color: t.featureIconColor }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: t.text }}>{item.label}</p>
                  <p className="text-xs" style={{ color: t.textMuted }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-10">
            {/* 1. Introducción */}
            <div>
              <h2 className="text-2xl font-bold mb-4" style={headingStyle}>1. Introducción</h2>
              <div className="space-y-3 text-sm leading-relaxed" style={textStyle}>
                <p>FitRewards es la aplicación de bienestar corporativo desarrollada por <strong style={strongStyle}>Motivy, Inc.</strong> Esta política describe cómo recopilamos, usamos, almacenamos y protegemos la información personal de los usuarios de FitRewards.</p>
                <p>Al utilizar FitRewards, usted acepta las prácticas descritas en esta política. Si no está de acuerdo, le recomendamos no usar la aplicación.</p>
              </div>
            </div>

            {/* 2. Datos que recopilamos */}
            <div className="pt-8 border-t" style={{ borderColor: t.borderColor }}>
              <h2 className="text-2xl font-bold mb-6" style={headingStyle}>2. Datos que recopilamos</h2>
              
              <div className="space-y-6">
                <div className="p-5 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone size={18} style={{ color: t.featureIconColor }} />
                    <h3 className="text-base font-semibold" style={headingStyle}>2.1 Datos de cuenta</h3>
                  </div>
                  <ul className="space-y-2 text-sm" style={textStyle}>
                    <li>• Nombre completo</li>
                    <li>• Correo electrónico institucional</li>
                    <li>• ID de usuario único</li>
                    <li>• Credenciales de acceso (encriptadas)</li>
                    <li>• Foto de perfil (opcional)</li>
                  </ul>
                </div>

                <div className="p-5 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Activity size={18} style={{ color: t.featureIconColor }} />
                    <h3 className="text-base font-semibold" style={headingStyle}>2.2 Datos de actividad física y salud</h3>
                  </div>
                  <p className="text-sm mb-3" style={textStyle}>Recopilados a través de Apple HealthKit, Google Fit y dispositivos wearables conectados:</p>
                  <ul className="space-y-2 text-sm" style={textStyle}>
                    <li>• Conteo de pasos diarios</li>
                    <li>• Calorías quemadas</li>
                    <li>• Distancia recorrida</li>
                    <li>• Frecuencia cardíaca</li>
                    <li>• Minutos de actividad física</li>
                    <li>• Datos de sueño (duración y calidad)</li>
                    <li>• Registro de alimentación (cuando el usuario lo ingresa)</li>
                  </ul>
                  <div className="mt-4 p-3 rounded-lg text-xs" style={{ background: t.featureIconBg, color: t.featureIconColor }}>
                    <strong>Importante:</strong> Los datos de salud solo se recopilan con su autorización expresa y pueden ser revocados en cualquier momento desde la configuración de la app.
                  </div>
                </div>

                <div className="p-5 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 size={18} style={{ color: t.featureIconColor }} />
                    <h3 className="text-base font-semibold" style={headingStyle}>2.3 Datos de uso de la aplicación</h3>
                  </div>
                  <ul className="space-y-2 text-sm" style={textStyle}>
                    <li>• Interacción con funcionalidades de la app</li>
                    <li>• Estadísticas de participación en retos</li>
                    <li>• Progreso y logros alcanzados</li>
                    <li>• Historial de canje de recompensas</li>
                    <li>• Rachas y consistencia de hábitos</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 3. Uso de los datos */}
            <div className="pt-8 border-t" style={{ borderColor: t.borderColor }}>
              <h2 className="text-2xl font-bold mb-4" style={headingStyle}>3. Cómo usamos sus datos</h2>
              <div className="space-y-3 text-sm leading-relaxed" style={textStyle}>
                <p><strong style={strongStyle}>Optimización de experiencia:</strong> Personalizar retos, recomendaciones de contenido y metas de bienestar basadas en su actividad.</p>
                <p><strong style={strongStyle}>Integración con servicios de bienestar:</strong> Sincronizar datos con wearables y apps de salud conectadas para ofrecer una experiencia unificada.</p>
                <p><strong style={strongStyle}>Métricas internas:</strong> Generar reportes agregados y anonimizados de bienestar organizacional para las empresas clientes.</p>
                <p><strong style={strongStyle}>Sistema de recompensas:</strong> Calcular puntos, rachas y logros basados en la actividad registrada.</p>
                <p><strong style={strongStyle}>Inteligencia artificial:</strong> Proporcionar insights personalizados, estimación de calorías y recomendaciones nutricionales.</p>
                <p><strong style={strongStyle}>Cumplimiento legal:</strong> Atender requerimientos legales y regulatorios aplicables.</p>
              </div>
            </div>

            {/* 4. Compartir datos */}
            <div className="pt-8 border-t" style={{ borderColor: t.borderColor }}>
              <h2 className="text-2xl font-bold mb-4" style={headingStyle}>4. Compartir datos con terceros</h2>
              <div className="space-y-3 text-sm leading-relaxed" style={textStyle}>
                <div className="p-4 rounded-lg border-l-4" style={{ borderColor: t.accent, background: t.featureIconBg }}>
                  <p className="font-semibold" style={{ color: t.text }}>Los datos de Apple HealthKit y Google Fit NO se comparten con terceros sin su consentimiento explícito.</p>
                </div>
                <p><strong style={strongStyle}>Empresa empleadora:</strong> Solo recibe datos agregados y anonimizados de participación (nunca datos individuales de salud).</p>
                <p><strong style={strongStyle}>Proveedores de servicio:</strong> Servicios técnicos necesarios para el funcionamiento de la app (hosting, analytics), bajo acuerdos estrictos de confidencialidad.</p>
                <p><strong style={strongStyle}>Nunca vendemos datos personales</strong> a terceros con fines publicitarios o comerciales.</p>
              </div>
            </div>

            {/* 5. Almacenamiento y seguridad */}
            <div className="pt-8 border-t" style={{ borderColor: t.borderColor }}>
              <h2 className="text-2xl font-bold mb-4" style={headingStyle}>5. Almacenamiento y seguridad</h2>
              <div className="space-y-3 text-sm leading-relaxed" style={textStyle}>
                <p>Los datos se almacenan en servidores seguros con encriptación en tránsito (TLS 1.3) y en reposo (AES-256).</p>
                <p>Implementamos medidas técnicas y organizativas para proteger sus datos contra acceso no autorizado, pérdida o alteración, incluyendo:</p>
                <ul className="space-y-2 ml-4">
                  <li>• Autenticación de dos factores para acceso administrativo</li>
                  <li>• Auditorías de seguridad periódicas</li>
                  <li>• Acceso basado en roles con principio de mínimo privilegio</li>
                  <li>• Monitoreo continuo de infraestructura</li>
                </ul>
              </div>
            </div>

            {/* 6. Derechos */}
            <div className="pt-8 border-t" style={{ borderColor: t.borderColor }}>
              <h2 className="text-2xl font-bold mb-4" style={headingStyle}>6. Sus derechos</h2>
              <div className="space-y-3 text-sm leading-relaxed" style={textStyle}>
                <p>Usted tiene derecho a:</p>
                <ul className="space-y-2 ml-4">
                  <li>• <strong style={strongStyle}>Acceder</strong> a sus datos personales almacenados</li>
                  <li>• <strong style={strongStyle}>Rectificar</strong> información incorrecta o incompleta</li>
                  <li>• <strong style={strongStyle}>Suprimir</strong> sus datos (derecho al olvido)</li>
                  <li>• <strong style={strongStyle}>Limitar</strong> el procesamiento de sus datos</li>
                  <li>• <strong style={strongStyle}>Portabilidad</strong> de datos en formato legible</li>
                  <li>• <strong style={strongStyle}>Revocar</strong> el consentimiento en cualquier momento</li>
                  <li>• <strong style={strongStyle}>Desconectar</strong> wearables y apps de salud desde la configuración</li>
                </ul>
                <p>Para ejercer cualquiera de estos derechos, contacte a:</p>
                <div className="p-4 rounded-xl border mt-4" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                  <p><strong style={strongStyle}>Email:</strong> info@motivy.co</p>
                  <p><strong style={strongStyle}>Teléfono:</strong> +1 305-788-1740</p>
                  <p className="text-xs mt-2" style={{ color: t.textMuted }}>Tiempo de respuesta: máximo 30 días hábiles</p>
                </div>
              </div>
            </div>

            {/* 7. Retención */}
            <div className="pt-8 border-t" style={{ borderColor: t.borderColor }}>
              <h2 className="text-2xl font-bold mb-4" style={headingStyle}>7. Retención de datos</h2>
              <div className="space-y-3 text-sm leading-relaxed" style={textStyle}>
                <p>Los datos personales se conservan mientras el usuario mantenga una cuenta activa en FitRewards. Al solicitar la eliminación de la cuenta, los datos se eliminan dentro de los 30 días siguientes, excepto aquellos que debamos conservar por obligaciones legales.</p>
              </div>
            </div>

            {/* 8. Menores */}
            <div className="pt-8 border-t" style={{ borderColor: t.borderColor }}>
              <h2 className="text-2xl font-bold mb-4" style={headingStyle}>8. Menores de edad</h2>
              <div className="space-y-3 text-sm leading-relaxed" style={textStyle}>
                <p>FitRewards no está dirigida a menores de 16 años. No recopilamos intencionalmente datos de menores. Si descubrimos que hemos recopilado datos de un menor, los eliminaremos inmediatamente.</p>
              </div>
            </div>

            {/* 9. Cambios */}
            <div className="pt-8 border-t" style={{ borderColor: t.borderColor }}>
              <h2 className="text-2xl font-bold mb-4" style={headingStyle}>9. Cambios en esta política</h2>
              <div className="space-y-3 text-sm leading-relaxed" style={textStyle}>
                <p>Nos reservamos el derecho de actualizar esta política periódicamente. Los cambios significativos serán notificados a través de la aplicación y/o por correo electrónico. El uso continuado de FitRewards después de los cambios constituye la aceptación de la política actualizada.</p>
              </div>
            </div>

            {/* Update date */}
            <div className="pt-8 border-t text-center" style={{ borderColor: t.borderColor }}>
              <p className="text-xs" style={{ color: t.textMuted }}>Última actualización: 28 de agosto de 2025</p>
              <div className="mt-4 flex justify-center gap-4">
                <Link to="/politica-de-privacidad" className="text-sm font-medium transition-colors duration-200" style={{ color: t.accent }}>
                  Política general de Motivy →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
